import { getBackendErrorCode, getNumericStatus } from './mutation-error';

export const TRANSCRIPT_UPLOAD_ACCEPT = '.vtt,.docx';
export const TRANSCRIPT_UPLOAD_MAX_BYTES = 10 * 1024 * 1024;

const ALLOWED_EXTENSIONS = TRANSCRIPT_UPLOAD_ACCEPT.split(',');

export type TranscriptFileError = 'unsupported_format' | 'too_large';

/** The checks the backend repeats, run first so a wrong file fails without an upload. */
export const validateTranscriptFile = (
  file: Pick<File, 'name' | 'size'>,
): TranscriptFileError | null => {
  const name = file.name.toLowerCase();
  if (!ALLOWED_EXTENSIONS.some((extension) => name.endsWith(extension))) {
    return 'unsupported_format';
  }
  return file.size > TRANSCRIPT_UPLOAD_MAX_BYTES ? 'too_large' : null;
};

const UPLOAD_ERROR_KEYS: Record<string, string> = {
  too_large: 'meeting-secretary.meetings.transcript-upload-error-too-large',
  unsupported_format: 'meeting-secretary.meetings.transcript-upload-error-unsupported-format',
  invalid_file: 'meeting-secretary.meetings.transcript-upload-error-invalid-file',
  no_speech_found: 'meeting-secretary.meetings.transcript-upload-error-no-speech',
  transcript_meeting_mismatch: 'meeting-secretary.meetings.transcript-upload-error-wrong-meeting',
  busy: 'meeting-secretary.meetings.transcript-upload-busy',
};
const GENERIC_UPLOAD_ERROR_KEY = 'meeting-secretary.meetings.transcript-upload-error-generic';

// A body that is not JSON (for example a 413 from the proxy in front of the API) has no code.
const STATUS_CODES: Record<number, string> = {
  413: 'too_large',
  415: 'unsupported_format',
  422: 'invalid_file',
};

export const getUploadErrorKeyForCode = (code: string | null): string =>
  (code && UPLOAD_ERROR_KEYS[code]) || GENERIC_UPLOAD_ERROR_KEY;

export const getUploadErrorKey = (error: unknown): string => {
  const status = getNumericStatus(error);
  const code = getBackendErrorCode(error) ?? (status ? (STATUS_CODES[status] ?? null) : null);
  return getUploadErrorKeyForCode(code);
};

export interface UploadErrorDetails {
  foundTitle?: string;
}

/** The meeting a rejected file belongs to, when the backend says which. */
export const getUploadErrorDetails = (error: unknown): UploadErrorDetails => {
  const data = (error as { data?: { detail?: Record<string, unknown> } } | null)?.data;
  const detail = data?.detail;
  const text = (value: unknown) => (typeof value === 'string' && value ? value : undefined);
  return { foundTitle: text(detail?.found_title) };
};
